import { useMemo, useState, type ReactNode } from "react";
import type { Dayjs } from "dayjs";

import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { formatCurrency } from "../../../utils/financialFormatters";
import { getAdminNow } from "../../../utils/dateService";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import { buildUserManagerUrl } from "../../../utils/NavigationUtils";
import "../../ReportingSummary/stylesReportingSummary.css";
import TopUsersPage from "../../TopUsers/TopUsers";
import type {
  TopUsersAppliedParams,
  TopUsersColumn,
  TopUsersFilterField,
  TopUsersKpiTile,
  TopUsersSelectOption,
  TopUsersViewOption,
} from "../../TopUsers/TopUsers";
import {
  useGetCasinoTopUsers,
  useGetCasinoTopUsersKpis,
  type CasinoTopUserRow,
  type CasinoTopUsersFilterDefinition,
  type CasinoTopUsersFilters,
  type CasinoTopUsersKpisData,
  type CasinoTopUsersView,
} from "../../../queries/casino-kpis/useGetCasinoTopUsers";

import styles from "./TopUsers.module.css";

// ─── Static fallbacks ────────────────────────────────────────────────────────
//
// `meta.filters` from the table response is the source of truth — these
// static lists only render on the first frame (before the BE response
// arrives) so the dropdowns aren't empty.
//
// Entries carry a `labelKey` (an AppStrings key) rather than a label:
// `setAppString` reassigns the exported binding when TCM strings land, so a
// module-level `label:` capture would freeze the bundled English. Labels are
// resolved inside the component's `useMemo`s (render time) instead. The
// `value` / `id` fields are API params and must stay locale-independent.

const DEFAULT_LIMIT = 20;

type LabelKeyedOption<V> = { value: V; labelKey: string };

const VIEW_DEFINITIONS: ReadonlyArray<{ id: string; labelKey: string }> = [
  { id: "top_winners", labelKey: "CASINO_TOP_USERS_VIEW_TOP_WINNERS" },
  { id: "top_losers", labelKey: "CASINO_TOP_USERS_VIEW_TOP_LOSERS" },
  { id: "top_turnover", labelKey: "CASINO_TOP_USERS_VIEW_TOP_TURNOVER" },
];

// The set of sizes the FE pre-labels ("{count} Users"); a BE-driven size
// outside this list keeps the BE's raw label.
const FALLBACK_LIMIT_VALUES: ReadonlyArray<number> = [10, 20, 50, 100];

const FALLBACK_PROVIDER_DEFINITIONS: ReadonlyArray<LabelKeyedOption<string>> = [
  { value: "pragmaticplay", labelKey: "CASINO_TOP_USERS_PROVIDER_PRAGMATIC_PLAY" },
  { value: "evolution", labelKey: "CASINO_TOP_USERS_PROVIDER_EVOLUTION" },
  { value: "netent", labelKey: "CASINO_TOP_USERS_PROVIDER_NETENT" },
  { value: "hacksaw", labelKey: "CASINO_TOP_USERS_PROVIDER_HACKSAW" },
  { value: "playngo", labelKey: "CASINO_TOP_USERS_PROVIDER_PLAYNGO" },
];

const FALLBACK_GAME_DEFINITIONS: ReadonlyArray<LabelKeyedOption<string>> = [
  { value: "sweet_bonanza", labelKey: "CASINO_TOP_USERS_GAME_SWEET_BONANZA" },
  { value: "gates_of_olympus", labelKey: "CASINO_TOP_USERS_GAME_GATES_OF_OLYMPUS" },
  { value: "big_bass_bonanza", labelKey: "CASINO_TOP_USERS_GAME_BIG_BASS_BONANZA" },
  { value: "book_of_dead", labelKey: "CASINO_TOP_USERS_GAME_BOOK_OF_DEAD" },
  { value: "crazy_time", labelKey: "CASINO_TOP_USERS_GAME_CRAZY_TIME" },
];

const FALLBACK_STATE_DEFINITIONS: ReadonlyArray<LabelKeyedOption<string>> = [
  { value: "CA", labelKey: "CASINO_TOP_USERS_STATE_CALIFORNIA" },
  { value: "NJ", labelKey: "CASINO_TOP_USERS_STATE_NEW_JERSEY" },
  { value: "NY", labelKey: "CASINO_TOP_USERS_STATE_NEW_YORK" },
  { value: "PA", labelKey: "CASINO_TOP_USERS_STATE_PENNSYLVANIA" },
];

const resolveLabel = (labelKey: string): string =>
  AppStrings[labelKey] ?? labelKey;

const toLabelledOptions = (
  definitions: ReadonlyArray<LabelKeyedOption<string>>,
): StringFilterOptions =>
  definitions.map(({ value, labelKey }) => ({
    value,
    label: resolveLabel(labelKey),
  }));

const formatLimitLabel = (count: number): string =>
  formatString(AppStrings.CASINO_TOP_USERS_LIMIT_OPTION, {
    count: String(count),
  });

// ─── Formatters ──────────────────────────────────────────────────────────────
//
// Currency formatting is brand-aware: the prefix symbol is resolved at
// render time via `useCurrencySymbol` inside the component (see
// `formatCurrency` defined inside the `columns` useMemo). The two
// formatters below are symbol-free and stay module-level because they
// don't depend on any per-render state.

const formatPercentage = (value: number, fractionDigits = 0) =>
  `${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;

const formatInteger = (value: number) =>
  Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

/**
 * Wraps a numeric cell value in a `<span>` whose text colour reflects
 * its sign — green for non-negative, red for negative — using the
 * shared `getColorBasedOnValue` helper (`var(--green-400)` /
 * `var(--red-400)`). Used for GGR and Margin % so a Top Winners table
 * row reads at a glance as red even before the operator parses the
 * number (since Top Winners = users winning against the house, GGR is
 * negative there).
 *
 * Null / undefined / non-finite values are treated as `0` (neutral
 * green) — the parser already coerces missing fields to `0`, so this
 * is mostly a defensive fallback against unexpected BE shapes.
 */
const renderColoredNumber = (
  value: unknown,
  formatter: (n: number) => string,
): ReactNode => {
  const numeric =
    typeof value === "number" && Number.isFinite(value) ? value : 0;
  return (
    // Stable testid so tests can grab the inner span without walking
    // the DOM (which would trip `testing-library/no-node-access`).
    // The testid is intentionally column-agnostic — duplicates across
    // rows/columns are resolved by scoping queries with `within(cell)`.
    <span
      style={{ color: getColorBasedOnValue(numeric) }}
      data-testid="top-users-colored-value"
    >
      {formatter(numeric)}
    </span>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ISO_DATE_FORMAT = "YYYY-MM-DD";

type StringFilterOptions = ReadonlyArray<TopUsersSelectOption<string>>;

type DateRange = [Dayjs, Dayjs];

/**
 * Dates live OUTSIDE `AppliedFilters` so they commit immediately on date
 * picker change (mirroring `Sportsbook/TopUsers` — the date prop flows
 * straight into the query keys and React Query refetches on its own,
 * without an Apply click). `limit` / `provider` / `game` / `state` stay
 * draft-only and only commit through `onApply`.
 */
type AppliedFilters = {
  limit: number;
  moneyTypes: string[];
  provider?: string;
  game?: string;
  state?: string;
};

const buildInitialDates = (): DateRange => {
  const now = getAdminNow();
  return [now.startOf("month"), now.endOf("day")];
};

const buildInitialAppliedFilters = (): AppliedFilters => ({
  limit: DEFAULT_LIMIT,
  moneyTypes: [],
  provider: undefined,
  game: undefined,
  state: undefined,
});

const findFilterDefinition = (
  filters: ReadonlyArray<CasinoTopUsersFilterDefinition>,
  id: string,
): CasinoTopUsersFilterDefinition | undefined =>
  filters.find((entry) => entry.id === id);

/**
 * Tooltip copy rendered behind the "i" icon at the top-right of every
 * non-rate KPI tile (TURNOVER, GGR, ROUNDS). MARGIN % is a rate so
 * the badge is always hidden and no tooltip applies.
 *
 * Wording mirrors `Sportsbook/TopUsers`'s `buildPercentChangeTooltip`
 * for cross-product consistency — kept as "percent change" even
 * though the underlying badge value now reads `pctOfTotal`.
 */
const buildPercentChangeTooltip = (metricName: string): string =>
  formatString(AppStrings.CASINO_TOP_USERS_PCT_CHANGE_TOOLTIP, {
    metric: metricName,
  });

const toStringOptions = (
  definition: CasinoTopUsersFilterDefinition | undefined,
  fallback: StringFilterOptions,
): StringFilterOptions => {
  if (!definition?.options.length) return fallback;
  return definition.options.map((opt) => ({
    value: String(opt.value),
    label: opt.label,
  }));
};

// Static label maps — BE returns the raw id / numeric value as the
// "label" (e.g. `"top_winners"`, `"20"`), so the FE owns the
// human-readable strings. BE drives the *set* of available options;
// the FE drives how each option reads.

const VIEW_LABEL_KEY_BY_ID = new Map(
  VIEW_DEFINITIONS.map((v) => [v.id, v.labelKey]),
);

const toLimitOptions = (
  definition: CasinoTopUsersFilterDefinition | undefined,
): ReadonlyArray<TopUsersSelectOption<number>> => {
  if (!definition?.options.length) {
    return FALLBACK_LIMIT_VALUES.map((value) => ({
      value,
      label: formatLimitLabel(value),
    }));
  }
  // BE may surface limit options as `"25"` or `25`; normalise to number so
  // the shared `Select<number>` round-trips the value correctly. The
  // human label ("20 Users") is FE-owned for the pre-labelled sizes —
  // falling back to the BE-provided string ("20") only for sizes we
  // haven't pre-labelled, so a new BE-driven size still renders.
  return definition.options
    .map((opt) => {
      const numeric = Number(opt.value);
      if (!Number.isFinite(numeric)) return null;
      return {
        value: numeric,
        label: FALLBACK_LIMIT_VALUES.includes(numeric)
          ? formatLimitLabel(numeric)
          : opt.label,
      };
    })
    .filter((opt): opt is TopUsersSelectOption<number> => opt !== null);
};

const toViewOptions = (
  definition: CasinoTopUsersFilterDefinition | undefined,
): ReadonlyArray<TopUsersViewOption> => {
  if (!definition?.options.length) {
    return VIEW_DEFINITIONS.map(({ id, labelKey }) => ({
      id,
      label: resolveLabel(labelKey),
    }));
  }
  // Preserve the static "Top Winners"/"Top Losers"/"Top Turnover" labels
  // — the BE returns the snake-case id as the label (e.g.
  // `"top_winners"`), which is fine for the wire but reads badly as a
  // tab. Unknown ids surfaced by a future BE change fall through to the
  // raw BE label so the new tab still appears.
  return definition.options.map((opt) => {
    const id = String(opt.value);
    const labelKey = VIEW_LABEL_KEY_BY_ID.get(id);
    return {
      id,
      label: labelKey ? resolveLabel(labelKey) : opt.label,
    };
  });
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Casino → Reports → Top Users.
 *
 * Thin wrapper around the shared `TopUsers` page (`src/components/TopUsers`).
 * Owns the Casino-specific concerns:
 *   • the three view tabs (`Top Winners`, `Top Losers`, `Top Turnover`),
 *   • the `Provider`, `Game`, and `State` single-select filters,
 *   • the KPI tile titles (Turnover, GGR) and table columns
 *     (Username, User ID, Turnover, GGR, Margin %, Rounds),
 *   • the data fetching (`/v2/casino/analytics/top-users` and its
 *     `/kpis` sibling, both gated by `casino.reports.top_users`).
 *
 * Filter / limit / view option lists prefer the BE-driven `meta.filters`
 * definitions from the table response and fall back to static lists on
 * the first render (before the BE payload arrives).
 *
 * Commit semantics:
 *   • `dates`     → refetch immediately on change (`onDatesChange`).
 *   • `view` tab  → refetch immediately on click (`onViewChange`).
 *   • `limit` / `provider` / `game` / `state` → draft inside the shared
 *     component; commit only on Apply (mirrors `Sportsbook/TopUsers`
 *     and the UtmReports / `AllManualActions` pattern where dates are
 *     immediate and panel filters are deferred).
 *
 * Route is gated solely by `casino.reports.top_users` via
 * `protectedElement` in `App.js` (per `AGENTS.md §10` — no
 * `developmentMode` escape hatch and no ad-hoc `FeatureFlagGuard`
 * wrapper in the route table). The matching `CASINO_OPTIONS` sider
 * entry mirrors that same permission with no additional whitelist.
 */
const TopUsers = () => {
  usePageTitle(AppStrings.TOP_USERS);
  const [activeView, setActiveView] =
    useState<CasinoTopUsersView>("top_winners");
  const [dates, setDates] = useState<DateRange>(buildInitialDates);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(
    buildInitialAppliedFilters,
  );

  const currencySymbol = useCurrencySymbol();

  // Shared query filters — both the KPIs and the table hook use the same
  // payload so they stay aligned per the BE contract (KPIs are the top-N
  // aggregate for the *current* view + filters; rows are the same set).
  // `start_date`/`end_date` are derived from `dates` directly so the
  // queries re-key (and refetch) the moment the date picker changes.
  const queryFilters: CasinoTopUsersFilters = useMemo(
    () => ({
      start_date: dates[0].format(ISO_DATE_FORMAT),
      end_date: dates[1].format(ISO_DATE_FORMAT),
      limit: appliedFilters.limit,
      view: activeView,
      money_type: appliedFilters.moneyTypes,
      provider: appliedFilters.provider,
      game: appliedFilters.game,
      state: appliedFilters.state,
    }),
    [activeView, dates, appliedFilters],
  );

  const tableQuery = useGetCasinoTopUsers(queryFilters);
  const kpisQuery = useGetCasinoTopUsersKpis(queryFilters);

  // Surface request failures as a banner above the toolbar — without
  // this, a 4xx/5xx silently renders an empty table + zero-value KPI
  // tiles, leaving the operator unable to tell "no data" from
  // "request failed". Copy attributes the failure to the affected
  // endpoint so the operator knows whether to retry, refresh, or
  // escalate.
  const errorMessage = (() => {
    if (tableQuery.isError && kpisQuery.isError) {
      return AppStrings.CASINO_TOP_USERS_LOAD_ERROR;
    }
    if (tableQuery.isError) {
      return AppStrings.CASINO_TOP_USERS_TABLE_LOAD_ERROR;
    }
    if (kpisQuery.isError) {
      return AppStrings.CASINO_TOP_USERS_KPIS_LOAD_ERROR;
    }
    return undefined;
  })();

  // ─── Filter definitions (BE-driven where available) ──────────────────────

  const filterDefinitions = useMemo(
    () => tableQuery.data?.filters ?? [],
    [tableQuery.data?.filters],
  );

  const limitOptions = useMemo(
    () => toLimitOptions(findFilterDefinition(filterDefinitions, "limit")),
    [filterDefinitions],
  );

  const viewTabs = useMemo(
    () => toViewOptions(findFilterDefinition(filterDefinitions, "view")),
    [filterDefinitions],
  );

  const providerOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "provider"),
        toLabelledOptions(FALLBACK_PROVIDER_DEFINITIONS),
      ),
    [filterDefinitions],
  );

  const currencyTypeOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "money_type"),
        [],
      ),
    [filterDefinitions],
  );

  const gameOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "game"),
        toLabelledOptions(FALLBACK_GAME_DEFINITIONS),
      ),
    [filterDefinitions],
  );

  const stateOptions = useMemo(
    () =>
      toStringOptions(
        findFilterDefinition(filterDefinitions, "state"),
        toLabelledOptions(FALLBACK_STATE_DEFINITIONS),
      ),
    [filterDefinitions],
  );

  const filters = useMemo<ReadonlyArray<TopUsersFilterField>>(
    () => [
      {
        id: "money_type",
        placeholder: AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES,
        options: [...currencyTypeOptions],
        mode: "multiple",
        defaultValue: [],
        testId: "top-users-money-type-select",
      },
      {
        id: "provider",
        placeholder: AppStrings.PROVIDER,
        options: [...providerOptions],
        allowClear: true,
        testId: "top-users-provider-select",
      },
      {
        id: "game",
        placeholder: AppStrings.GAME,
        options: [...gameOptions],
        allowClear: true,
        testId: "top-users-game-select",
      },
      {
        id: "state",
        placeholder: AppStrings.STATE,
        options: [...stateOptions],
        allowClear: true,
        testId: "top-users-state-select",
      },
    ],
    [currencyTypeOptions, providerOptions, gameOptions, stateOptions],
  );

  // ─── KPI tiles ───────────────────────────────────────────────────────────
  //
  // BE returns `turnover`, `ggr`, `margin_pct`, and `rounds` per the
  // current Casino contract (any entry can come back as `null`, which
  // the parser collapses to the zero-value `EMPTY_KPI` so this code can
  // dereference safely).
  //
  // Badge source: every tile reads `pctOfTotal` (share-of-total —
  // "how concentrated is this metric in the top-N"), per product.
  // MARGIN % surfaces `null` (a rate has no meaningful share-of-total)
  // — the wrapper coerces `null → undefined` so `KpiCard` hides the
  // badge instead of rendering "0%".
  //
  // `percentageMaximumFractionDigits: 2` is pinned on every tile that
  // can surface a badge so a sub-1% share (e.g. `0.07`) survives the
  // integer-precision tiles (ROUNDS sets `maximumFractionDigits: 0`
  // on the main value) without rounding down to `"0%"`.
  //
  // Mirrors `Sportsbook/TopUsers`.

  const kpis = useMemo<ReadonlyArray<TopUsersKpiTile>>(() => {
    const data: CasinoTopUsersKpisData | undefined = kpisQuery.data;
    const turnover = data?.turnover;
    const ggr = data?.ggr;
    const marginPct = data?.margin_pct;
    const rounds = data?.rounds;
    return [
      {
        key: "turnover",
        title: AppStrings.TURNOVER,
        titleTooltip: buildPercentChangeTooltip(
          AppStrings.CASINO_TOP_USERS_METRIC_TURNOVER,
        ),
        value: turnover?.value ?? 0,
        percentage: turnover?.pctOfTotal ?? undefined,
        currencySymbol,
        maximumFractionDigits: 2,
        percentageMaximumFractionDigits: 2,
      },
      {
        key: "ggr",
        title: AppStrings.GGR,
        titleTooltip: buildPercentChangeTooltip(
          AppStrings.CASINO_TOP_USERS_METRIC_GGR,
        ),
        value: ggr?.value ?? 0,
        percentage: ggr?.pctOfTotal ?? undefined,
        currencySymbol,
        maximumFractionDigits: 2,
        percentageMaximumFractionDigits: 2,
      },
      {
        key: "margin_pct",
        title: AppStrings.MARGIN_PCT,
        // Intentionally no `titleTooltip` — a rate has no meaningful
        // share-of-total signal, so the badge is always hidden.
        value: marginPct?.value ?? 0,
        percentage: marginPct?.pctOfTotal ?? undefined,
        rate: true,
        maximumFractionDigits: 2,
      },
      {
        key: "rounds",
        title: AppStrings.ROUNDS,
        // Rounds is an absolute count, so a share-of-total signal
        // applies. Currency icon stays off — Rounds isn't monetary.
        titleTooltip: buildPercentChangeTooltip(
          AppStrings.CASINO_TOP_USERS_METRIC_ROUNDS,
        ),
        value: rounds?.value ?? 0,
        percentage: rounds?.pctOfTotal ?? undefined,
        // Rounds count is integer. The badge, however, is a fractional
        // share (e.g. `0.07`), so override the badge precision back to
        // 2 — without it `0.07%` rounds to `0%`.
        maximumFractionDigits: 0,
        percentageMaximumFractionDigits: 2,
      },
    ];
  }, [currencySymbol, kpisQuery.data]);

  // ─── Table ───────────────────────────────────────────────────────────────

  // Sort icons are intentionally omitted on every column — the rows are
  // already returned sorted by the BE per the active `view` (Top
  // Winners / Top Losers / Top Turnover), so a per-column resort would
  // contradict the tab's semantics ("top by what?"). Dropping `sorter`
  // tells antd not to render the sort affordance at all.
  const columns = useMemo<ReadonlyArray<TopUsersColumn<CasinoTopUserRow>>>(() => {
    const money = (value: number) =>
      formatCurrency(Number(value ?? 0), currencySymbol, 2);

    return [
      {
        title: AppStrings.USERNAME,
        dataIndex: "username",
        key: "username",
        width: 160,
        // Username always links to User Overview by `user_id` per the BE
        // contract — even when `username` is null the cell stays clickable
        // (the label falls back to `userId` in the parser).
        render: (_value, row) => (
          <a
            href={buildUserManagerUrl(row.userId)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.usernameLink}
            data-testid={`top-users-username-link-${row.userId}`}
          >
            {row.username || row.userId}
          </a>
        ),
      },
      {
        title: AppStrings.USER_ID,
        dataIndex: "userId",
        key: "userId",
        width: 120,
        ellipsis: true,
      },
      {
        title: AppStrings.TURNOVER,
        dataIndex: "turnover",
        key: "turnover",
        width: 140,
        // Two decimals to stay aligned with the Turnover / GGR KPI tiles
        // above the table (`maximumFractionDigits: 2`) — without this
        // the same currency reads as `$32,991` in the cell and
        // `$32,991.00` in the tile, which operators consistently
        // flagged as broken (same fix as `Sportsbook/TopUsers`).
        render: (value) => money(value as number),
      },
      {
        title: AppStrings.GGR,
        dataIndex: "ggr",
        key: "ggr",
        width: 140,
        // Coloured by sign — Top Winners rows tend to render red here
        // (GGR < 0 means users net-won against the house). Turnover
        // stays the default colour; it can only be ≥ 0.
        render: (value) =>
          renderColoredNumber(value, (n) => money(n)),
      },
      {
        title: AppStrings.MARGIN_PCT,
        dataIndex: "marginPct",
        key: "marginPct",
        width: 140,
        render: (value) =>
          renderColoredNumber(value, (n) => formatPercentage(n, 2)),
      },
      {
        title: AppStrings.ROUNDS,
        dataIndex: "rounds",
        key: "rounds",
        width: 120,
        render: (value) => formatInteger(value as number),
      },
    ];
  }, [currencySymbol]);

  // Apply only commits the deferred filters
  // (`limit` / `provider` / `game` / `state`). Dates were already
  // committed via `onDatesChange`, so `params.dates` is intentionally
  // ignored — re-committing here would just produce a no-op `setDates`
  // call with the same value.
  const handleApply = (params: TopUsersAppliedParams) => {
    const limit = (params.filters.limit as number | undefined) ?? DEFAULT_LIMIT;
    const moneyTypes = Array.isArray(params.filters.money_type)
      ? params.filters.money_type.map(String)
      : [];
    setAppliedFilters({
      limit,
      moneyTypes,
      provider: (params.filters.provider as string | undefined) || undefined,
      game: (params.filters.game as string | undefined) || undefined,
      state: (params.filters.state as string | undefined) || undefined,
    });
  };

  // Drives the Clear Filters button visibility (it stays hidden until at
  // least one filter is actually narrowing the query — same semantic as
  // `Sportsbook/TopUsers.hasAppliedFilters`). Keyed off *applied* state,
  // not the draft, so an unapplied draft selection doesn't surface a
  // button that has nothing to clear yet. The view tab and the date
  // range are intentionally excluded — they're navigation / primary
  // controls, not filters.
  const hasAppliedFilters =
    appliedFilters.limit !== DEFAULT_LIMIT ||
    appliedFilters.moneyTypes.length > 0 ||
    appliedFilters.provider !== undefined ||
    appliedFilters.game !== undefined ||
    appliedFilters.state !== undefined;

  // Mirrors the shared component's draft reset on the wrapper's applied
  // side. The shared component has already wiped its draft dropdowns
  // by the time this fires.
  const handleClear = () => {
    setAppliedFilters(buildInitialAppliedFilters());
  };

  return (
    <TopUsersPage<CasinoTopUserRow>
      product={SIDER_TABS?.CASINO}
      hideTitle
      showDatePicker
      initialDates={dates}
      onDatesChange={setDates}
      views={viewTabs}
      activeView={activeView}
      onViewChange={(id) => setActiveView(id as CasinoTopUsersView)}
      filters={filters}
      limitOptions={limitOptions}
      defaultLimit={DEFAULT_LIMIT}
      onApply={handleApply}
      onClear={handleClear}
      showClear={hasAppliedFilters}
      kpis={kpis}
      // Keep the two loading states independent so the table and the
      // KPI row don't blink together when only one of the queries is
      // in flight (they have separate cache keys + endpoints).
      kpisLoading={kpisQuery.isFetching}
      columns={columns}
      rows={tableQuery.data?.rows ?? []}
      loading={tableQuery.isFetching}
      errorMessage={errorMessage}
      // Match the Reporting Summary table skin verbatim — same skin
      // class on the antd `<Table>` + zebra-striped row class on
      // every other row. Combined with the side-effect CSS import
      // above, this is what makes the two tables visually identical.
      tableClassName="reporting-summary-table"
      rowClassName={(_record: CasinoTopUserRow, index: number) =>
        index % 2 === 1 ? "reporting-summary-table-row--alt" : ""
      }
    />
  );
};

export default TopUsers;
